const productRecommendationsGlobalCache = {};
const productRecommendationsPopulatedIds = new Set();

/**
 * @returns {boolean}
 */
function isDesignMode() {
  return Boolean(window.Shopify?.designMode);
}

class ProductRecommendations extends HTMLElement {
  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // Setting changes in the Theme Editor must re-fetch (layout, colors, etc.)
      if (isDesignMode()) {
        this.#invalidateForReload();
      }

      this.#loadRecommendations();
      break;
    }
  });

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#mutationObserver.observe(this, { attributes: true });

    // Theme Editor re-injects the section without cards; clear guards so we can fill again.
    if (isDesignMode()) {
      this.#invalidateForReload();
    }

    this.#loadRecommendations();
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
    this.#activeFetch = null;

    if (isDesignMode() && this.id) {
      productRecommendationsPopulatedIds.delete(this.id);
    }
  }

  /**
   * Allow a fresh populate after section re-render (editor) or empty DOM.
   */
  #invalidateForReload() {
    if (this.id) {
      productRecommendationsPopulatedIds.delete(this.id);
    }

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    for (const key of Object.keys(productRecommendationsGlobalCache)) {
      if (key.includes(`section_id=${sectionId}`)) {
        delete productRecommendationsGlobalCache[key];
      }
    }
  }

  /**
   * @returns {boolean}
   */
  #hasRenderedCards() {
    return Boolean(this.querySelector('.resource-list__item, .resource-list__carousel'));
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!id) {
      throw new Error('An ID attribute is required');
    }

    // Theme editor visual preview / pages without a product: Liquid already
    // rendered placeholder cards — do not fetch or throw.
    if (
      this.hasAttribute('data-shopify-visual-preview') ||
      this.hasAttribute('data-shopify-editor-preview') ||
      !productId
    ) {
      return;
    }

    // Already rendered by Liquid (incl. Theme Editor after a successful section render)
    if (recommendationsPerformed === 'true' && this.#hasRenderedCards()) {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!isDesignMode()) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        // Skip only if we already populated AND cards are still in the DOM
        if (productRecommendationsPopulatedIds.has(id) && this.#hasRenderedCards()) {
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          productRecommendationsPopulatedIds.add(id);
          this.innerHTML = recommendations.innerHTML;
          requestAnimationFrame(() => {
            this.dataset.recommendationsPerformed = 'true';
          });
        } else if (!isDesignMode()) {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        // AbortError is expected when a newer fetch supersedes this one
        if (e?.name === 'AbortError') return;
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    // Never serve stale HTML while editing — setting changes must appear immediately
    if (!isDesignMode()) {
      const cachedResponse = productRecommendationsGlobalCache[url];
      if (cachedResponse) {
        return { success: true, data: cachedResponse };
      }
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      if (!isDesignMode()) {
        productRecommendationsGlobalCache[url] = text;
      }
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way.
   * Do not hide the entire section so the heading and structure remain visible after save.
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.dataset.error = 'Error loading product recommendations';
    this.classList.add('product-recommendations--error');
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
